import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, lastValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  UploadControlResponse,
  UploadStatusRow,
  AnyDeskInfoResponse,
  AnyDeskPasswordResponse,
  AnyDeskUpdate,
  AnyDeskCreate,
  ErrorControlResponse,
  FingerprintCreateUpdate,
  FingerprintInfoResponse,
  FingerprintPuestosResponse,
  HeartbeatInfoResponse,
  HeartbeatAsistenteResponse,
  OrderRegisterResponse,
  PackageInfoResponse,
  PharmaInfoResponse,
  TaskProgramResponse,
  PharmaInfoStructure,
  StatusControlResponse,
  RegisterOrder,
  PharmacyProcessesStructure,
  PackageConfigurationRequest,
  PackageInfoStructure,
  PharmaGroupCatalog,
  PharmaGroupCatalogRequest,
  PharmaGroupCatalogResponse,
  PharmaGroupConfigurationResponse,
  PharmaGroupMembership,
  PharmaGroupMembershipRequest,
  PharmaGroupMembershipResponse,
  PharmaGroupSelection,
  OrderRegisterStructure,
  TaskProgDeleteRequest,
  Message,
  PollingResponse,
  ConversationList,
  PartialUploadResponse,
  LogResponse,
  Municipio,
  GenerateIdResponse,
  PharmaInfoCreate,
  PlaceIdResponse,
  MapCoords,
  MapCoordsCompetencia,
  FarmaIaMetadataEntry,
  FarmaIaMetadataResponse,
  FarmaIaTrainingEntry,
  FarmaIaTrainingResponse,
  UsageDataEntry,
  UsageDataResponse,
  TrainingUpdateRequest,
  ListadoNombres,
  VacationCalendarResponse,
  VacationDay,
  VacationDayCreate,
  VacationDayRangeCreate,
  VacationPerson,
  VacationPersonCreate,
  VacationPersonUpdate,
  VacationSummary
} from '../models/interfaz';
import { ApiUrlService } from './api-url.service';
import { AlertService, AlertMessage } from './alert.service';
import { AuthserviceService } from './authservice.service';
import { DebugService } from './debug.service';
import { FarmaIaService } from './farma-ia.service';
import { LoggerService } from './logger.service';
import { UploadService } from './upload.service';
import { WhatsappService } from './whatsapp.service';
import { formatDateYmd, formatTimeHms } from '../utils/date-utils';

export type { AlertMessage } from './alert.service';

@Injectable({
  providedIn: 'root'
})
export class ServiceService {

  private municipiosCache: { data: Municipio[]; timestamp: number } | null = null;
  private readonly CACHE_TTL = 2 * 60 * 1000; // 2 minutos en ms

  constructor(
    private apiUrl: ApiUrlService,
    private http: HttpClient,
    private alertService: AlertService,
    private authService: AuthserviceService,
    private debugService: DebugService,
    private farmaIaService: FarmaIaService,
    private uploadService: UploadService,
    private whatsappService: WhatsappService,
    private logger: LoggerService
  ) { }

  private __executionReg = new BehaviorSubject<OrderRegisterResponse | undefined>(undefined);
  private __packageInfo = new BehaviorSubject<PackageInfoResponse | undefined>(undefined);
  private __fingerprintInfo = new BehaviorSubject<FingerprintInfoResponse | undefined>(undefined);
  private __fingerprintUnycopInfo = new BehaviorSubject<FingerprintInfoResponse | undefined>(undefined);
  private __fingerprintFarmanagerInfo = new BehaviorSubject<FingerprintInfoResponse | undefined>(undefined);
  private __heartbeatInfo = new BehaviorSubject<HeartbeatInfoResponse | undefined>(undefined);
  private __heartbeatUnycopInfo = new BehaviorSubject<HeartbeatInfoResponse | undefined>(undefined);
  private __anydeskInfo = new BehaviorSubject<AnyDeskInfoResponse | undefined>(undefined);
  private __infoPharma = new BehaviorSubject<PharmaInfoResponse | undefined>(undefined);
  public infoPharma$ = this.__infoPharma.asObservable();
  private __globalStatus = new BehaviorSubject<StatusControlResponse | undefined>(undefined);
  private __ProgramacionTarea = new BehaviorSubject<TaskProgramResponse | undefined>(undefined);
  private __ProgramacionTareaV2 = new BehaviorSubject<TaskProgramResponse | undefined>(undefined);

  public get errorEstatus(): BehaviorSubject<ErrorControlResponse | undefined> {
    return this.debugService.errorEstatus;
  }

  public set errorEstatus(value: ErrorControlResponse) {
    this.debugService.errorEstatus = value;
  }

  public get executionReg(): BehaviorSubject<OrderRegisterResponse | undefined> {
    return this.__executionReg;
  }

  public set executionReg(value: OrderRegisterResponse) {
    this.__executionReg.next(value);
  }

  public get uploadControl(): BehaviorSubject<UploadControlResponse | undefined> {
    return this.uploadService.uploadControl;
  }

  public set uploadControl(value: UploadControlResponse) {
    this.uploadService.uploadControl.next(value);
  }

  public get uploadStatusDatabricks(): BehaviorSubject<UploadStatusRow[] | undefined> {
    return this.uploadService.uploadStatusDatabricks;
  }

  public set uploadStatusDatabricks(value: UploadStatusRow[]) {
    this.uploadService.uploadStatusDatabricks.next(value);
  }

  public get packageInfo(): BehaviorSubject<PackageInfoResponse | undefined> {
    return this.__packageInfo;
  }

  public set packageInfo(value: PackageInfoResponse) {
    this.__packageInfo.next(value);
  }

  public get fingerprintInfo(): BehaviorSubject<FingerprintInfoResponse | undefined> {
    return this.__fingerprintInfo;
  }

  public set fingerprintInfo(value: FingerprintInfoResponse) {
    this.__fingerprintInfo.next(value);
  }

  public get fingerprintUnycopInfo(): BehaviorSubject<FingerprintInfoResponse | undefined> {
    return this.__fingerprintUnycopInfo;
  }

  public set fingerprintUnycopInfo(value: FingerprintInfoResponse) {
    this.__fingerprintUnycopInfo.next(value);
  }

  public get fingerprintFarmanagerInfo(): BehaviorSubject<FingerprintInfoResponse | undefined> {
    return this.__fingerprintFarmanagerInfo;
  }

  public set fingerprintFarmanagerInfo(value: FingerprintInfoResponse) {
    this.__fingerprintFarmanagerInfo.next(value);
  }

  public get heartbeatInfo(): BehaviorSubject<HeartbeatInfoResponse | undefined> {
    return this.__heartbeatInfo;
  }

  public set heartbeatInfo(value: HeartbeatInfoResponse) {
    this.__heartbeatInfo.next(value);
  }

  public get heartbeatUnycopInfo(): BehaviorSubject<HeartbeatInfoResponse | undefined> {
    return this.__heartbeatUnycopInfo;
  }

  public set heartbeatUnycopInfo(value: HeartbeatInfoResponse) {
    this.__heartbeatUnycopInfo.next(value);
  }

  public get anydeskInfo(): BehaviorSubject<AnyDeskInfoResponse | undefined> {
    return this.__anydeskInfo;
  }

  public set anydeskInfo(value: AnyDeskInfoResponse) {
    this.__anydeskInfo.next(value);
  }

  public get infoPharma(): BehaviorSubject<PharmaInfoResponse | undefined> {
    return this.__infoPharma;
  }

  public set infoPharma(value: PharmaInfoResponse) {
    this.__infoPharma.next(value);
  }

  public get globalStatus(): BehaviorSubject<StatusControlResponse | undefined> {
    return this.__globalStatus;
  }

  public set globalStatus(value: StatusControlResponse) {
    this.__globalStatus.next(value);
  }

  public set ProgramacionTarea(value: TaskProgramResponse) {
    this.__ProgramacionTarea.next(value);
  }

  public get ProgramacionTarea(): BehaviorSubject<TaskProgramResponse | undefined> {
    return this.__ProgramacionTarea;
  }

  public set ProgramacionTareaV2(value: TaskProgramResponse) {
    this.__ProgramacionTareaV2.next(value);
  }

  public get ProgramacionTareaV2(): BehaviorSubject<TaskProgramResponse | undefined> {
    return this.__ProgramacionTareaV2;
  }

  public async getProgramacionTareas() {
    const url = `${this.apiUrl.programacionTareasAPI}`;
    const datos = await lastValueFrom(this.http.get<TaskProgramResponse>(url));
    this.__ProgramacionTarea.next(datos);
    return this.__ProgramacionTarea.asObservable();
  }

  public async getProgramacionTareasV2() {
    const url = `${this.apiUrl.programacionTareasV2API}`;
    const datos = await lastValueFrom(this.http.get<TaskProgramResponse>(url));
    this.__ProgramacionTareaV2.next(datos);
    return this.__ProgramacionTareaV2.asObservable();
  }

  public async insertProgramacionTareas(data: PharmacyProcessesStructure): Promise<string> {
    const url = `${this.apiUrl.insertProgramacionTareasAPI}`;
    const datos = await lastValueFrom(this.http.put<string>(url, data));
    return datos;
  }

  public async insertProgramacionTareasV2(data: PharmacyProcessesStructure): Promise<string> {
    const url = `${this.apiUrl.insertProgramacionTareasV2API}`;
    const datos = await lastValueFrom(this.http.put<string>(url, data));
    return datos;
  }

  public async updateProgramacionTareas(data: PharmacyProcessesStructure): Promise<string> {
    const url = `${this.apiUrl.updateProgramacionTareasAPI}`;
    const datos = await lastValueFrom(this.http.put<string>(url, data));
    return datos;
  }

  public async updateProgramacionTareasV2(data: PharmacyProcessesStructure): Promise<string> {
    const url = `${this.apiUrl.updateProgramacionTareasV2API}`;
    const datos = await lastValueFrom(this.http.put<string>(url, data));
    return datos;
  }

  public async deleteProgramacionTareas(data: TaskProgDeleteRequest): Promise<string> {
    const url = `${this.apiUrl.deleteProgramacionTareasAPI}`;
    const datos = await lastValueFrom(
      this.http.delete<string>(url, { body: data })  // 👈 importante: usar { body: ... }
    );
    return datos;
  }

  public async deleteProgramacionTareasV2(data: TaskProgDeleteRequest): Promise<string> {
    const url = `${this.apiUrl.deleteProgramacionTareasV2API}`;
    const datos = await lastValueFrom(
      this.http.delete<string>(url, { body: data })
    );
    return datos;
  }



  public async getGlobalStatus() {
    if (this.__globalStatus.value !== undefined) {
      return this.__globalStatus.asObservable();
    } else {
      const url = `${this.apiUrl.globalStatusApi}`;
      const datos = await lastValueFrom(this.http.get<StatusControlResponse>(url));
      this.__globalStatus.next(datos);
      return this.__globalStatus.asObservable();
    }
  }

  public async getInfoPharma(): Promise<void> {
    if (!this.authService.isLoggedIn) {
      return;
    }

    const url = `${this.apiUrl.infoPharmaApi}`;
    const datos = await lastValueFrom(this.http.get<PharmaInfoResponse>(url));
    this.__infoPharma.next(datos);
  }

  public async getPackageInfo() {
    const url = `${this.apiUrl.packageInfoApi}`;
    const datos = await lastValueFrom(this.http.get<PackageInfoResponse>(url));
    this.__packageInfo.next(datos);
    return this.__packageInfo.asObservable();
  }

  public async getFingerprintInfo() {
    const url = `${this.apiUrl.fingerprintApi}`;
    const datos = await lastValueFrom(this.http.get<FingerprintInfoResponse>(url));
    this.__fingerprintInfo.next(datos);
    return this.__fingerprintInfo.asObservable();
  }

  public async getFingerprintUnycopInfo() {
    const url = `${this.apiUrl.fingerprintUnycopApi}`;
    const datos = await lastValueFrom(this.http.get<FingerprintInfoResponse>(url));
    this.__fingerprintUnycopInfo.next(datos);
    return this.__fingerprintUnycopInfo.asObservable();
  }

  public async getFingerprintFarmanagerInfo() {
    const url = `${this.apiUrl.fingerprintFarmanagerApi}`;
    const datos = await lastValueFrom(this.http.get<FingerprintInfoResponse>(url));
    this.__fingerprintFarmanagerInfo.next(datos);
    return this.__fingerprintFarmanagerInfo.asObservable();
  }

  public async getHeartbeatInfo() {
    const url = `${this.apiUrl.heartbeatApi}`;
    const datos = await lastValueFrom(this.http.get<HeartbeatInfoResponse>(url));
    this.__heartbeatInfo.next(datos);
    return this.__heartbeatInfo.asObservable();
  }

  public async getHeartbeatUnycopInfo() {
    const url = `${this.apiUrl.heartbeatUnycopApi}`;
    const datos = await lastValueFrom(this.http.get<HeartbeatInfoResponse>(url));
    this.__heartbeatUnycopInfo.next(datos);
    return this.__heartbeatUnycopInfo.asObservable();
  }

  public getFingerprintPuestosInfo(): Promise<FingerprintPuestosResponse> {
    return lastValueFrom(this.http.get<FingerprintPuestosResponse>(this.apiUrl.fingerprintPuestosApi));
  }

  public getHeartbeatAsistenteInfo(): Promise<HeartbeatAsistenteResponse> {
    return lastValueFrom(this.http.get<HeartbeatAsistenteResponse>(this.apiUrl.heartbeatAsistenteApi));
  }

  public async getAnyDeskInfo() {
    const url = `${this.apiUrl.anydeskApi}`;
    const datos = await lastValueFrom(this.http.get<AnyDeskInfoResponse>(url));
    this.__anydeskInfo.next(datos);
    return this.__anydeskInfo.asObservable();
  }

  public async updateAnyDesk(idFarma: string, idPuesto: string, data: AnyDeskUpdate): Promise<void> {
    const url = `${this.apiUrl.updateAnydeskApi}/${encodeURIComponent(idFarma)}/${encodeURIComponent(idPuesto)}`;
    await lastValueFrom(this.http.put<AnyDeskUpdate>(url, data));
  }

  public async addAnyDesk(data: AnyDeskCreate): Promise<void> {
    const url = `${this.apiUrl.addAnydeskApi}`;
    await lastValueFrom(this.http.post<AnyDeskCreate>(url, data));
  }

  public async deleteAnyDesk(idFarma: string, idPuesto: string): Promise<void> {
    const url = `${this.apiUrl.deleteAnydeskApi}/${encodeURIComponent(idFarma)}/${encodeURIComponent(idPuesto)}`;
    await lastValueFrom(this.http.delete<void>(url));
  }

  public async revealAnyDeskPassword(idFarma: string, idPuesto: string): Promise<AnyDeskPasswordResponse> {
    const url = `${this.apiUrl.anydeskPasswordApi}/${encodeURIComponent(idFarma)}/${encodeURIComponent(idPuesto)}`;
    return lastValueFrom(this.http.get<AnyDeskPasswordResponse>(url));
  }

  public async insertFingerprint(data: FingerprintCreateUpdate): Promise<void> {
    await lastValueFrom(this.http.post<FingerprintCreateUpdate>(this.apiUrl.fingerprintApi, data));
  }

  public async insertFingerprintUnycop(data: FingerprintCreateUpdate): Promise<void> {
    await lastValueFrom(this.http.post<FingerprintCreateUpdate>(this.apiUrl.fingerprintUnycopApi, data));
  }

  public async insertFingerprintFarmanager(data: FingerprintCreateUpdate): Promise<void> {
    await lastValueFrom(this.http.post<FingerprintCreateUpdate>(this.apiUrl.fingerprintFarmanagerApi, data));
  }

  public async updateFingerprint(idFarma: string, data: FingerprintCreateUpdate): Promise<void> {
    await lastValueFrom(this.http.put<FingerprintCreateUpdate>(`${this.apiUrl.fingerprintApi}/${idFarma}`, data));
  }

  public async updateFingerprintUnycop(idFarma: string, data: FingerprintCreateUpdate): Promise<void> {
    await lastValueFrom(this.http.put<FingerprintCreateUpdate>(`${this.apiUrl.fingerprintUnycopApi}/${idFarma}`, data));
  }

  public async updateFingerprintFarmanager(idFarma: string, data: FingerprintCreateUpdate): Promise<void> {
    await lastValueFrom(this.http.put<FingerprintCreateUpdate>(`${this.apiUrl.fingerprintFarmanagerApi}/${idFarma}`, data));
  }

  public async deleteFingerprint(idFarma: string): Promise<void> {
    await lastValueFrom(this.http.delete<void>(`${this.apiUrl.fingerprintApi}/${idFarma}`));
  }

  public async deleteFingerprintUnycop(idFarma: string): Promise<void> {
    await lastValueFrom(this.http.delete<void>(`${this.apiUrl.fingerprintUnycopApi}/${idFarma}`));
  }

  public async deleteFingerprintFarmanager(idFarma: string): Promise<void> {
    await lastValueFrom(this.http.delete<void>(`${this.apiUrl.fingerprintFarmanagerApi}/${idFarma}`));
  }

  public async insertPackage(data: PackageInfoStructure): Promise<void> {
    try {
      await lastValueFrom(this.http.post<PackageInfoStructure>(this.apiUrl.insertPackageApi, data));
      // Aquí podrías optar por emitir una alerta de éxito directamente:
      // this.showAlert({ type: 'success', summary: 'Éxito', detail: 'Farmacia agregada con éxito' });
    } catch (error) {
      this.logger.error('Error al agregar farmacia:', error);
      // También podrías emitir la alerta de error aquí:
      // this.showAlert({ type: 'error', summary: 'Error', detail: 'Error al agregar farmacia' });
      throw error;
    }
  }

  public async savePackageConfiguration(
    packageInfo: PackageInfoStructure,
    groups: PharmaGroupSelection[]
  ): Promise<void> {
    const request: PackageConfigurationRequest = { package_info: packageInfo, groups };
    await lastValueFrom(this.http.post<void>(this.apiUrl.savePackageConfigurationApi, request));
  }

  public async getGroupConfiguration(idFarma: string): Promise<PharmaGroupConfigurationResponse> {
    return lastValueFrom(
      this.http.get<PharmaGroupConfigurationResponse>(
        `${this.apiUrl.groupConfigurationApi}/${encodeURIComponent(idFarma)}`
      )
    );
  }



  public async getErrorStatus() {
    return this.debugService.getErrorStatus();
  }
  public async getExecutionReg() {
    const url = `${this.apiUrl.executionRegApi}`;
    const datos = await lastValueFrom(this.http.get<OrderRegisterResponse>(url));
    this.__executionReg.next(datos);
    return this.__executionReg.asObservable();
  }

  public async deleteExecutionReg(orden: OrderRegisterStructure) {
    try {
      await lastValueFrom(
        this.http.request('delete', `${this.apiUrl.deleteExecutionRegApi}`, {
          body: orden,
        })
      );
      // Puedes emitir aquí la alerta de éxito si quieres
    } catch (error) {
      this.logger.error('Error al eliminar orden:', error);
      throw error;
    }
  }


  public async getUploadControl() {
    return this.uploadService.getUploadControl();
  }

  // Método para agregar farmacia
  public async addPharmacy(data: PharmaInfoCreate): Promise<PharmaInfoStructure> {
    try {
      const created = await lastValueFrom(
        this.http.post<PharmaInfoStructure>(this.apiUrl.addPharmacyApi, data)
      );
      return created;
      // Aquí podrías optar por emitir una alerta de éxito directamente:
      // this.showAlert({ type: 'success', summary: 'Éxito', detail: 'Farmacia agregada con éxito' });
    } catch (error) {
      this.logger.error('Error al agregar farmacia:', error);
      // También podrías emitir la alerta de error aquí:
      // this.showAlert({ type: 'error', summary: 'Error', detail: 'Error al agregar farmacia' });
      throw error;
    }
  }

  // LOGICA ANTIGUA DE GET MUNICIPIOS

  // Devuelve los municipios, usando cache si no han pasado 2 minutos (para el dropdown)
  // public async getMunicipios(): Promise<Municipio[]> {
  //   const now = Date.now();
  //   if (this.municipiosCache && now - this.municipiosCache.timestamp < this.CACHE_TTL) {
  //     return this.municipiosCache.data;
  //   }
  //   // Si no hay cache o expiró:
  //   const result$ = this.http.get<Municipio[]>(this.apiUrl.getMunicipiosApi);
  //   const list = await lastValueFrom(result$);
  //   this.municipiosCache = { data: list, timestamp: now };
  //   return list;
  // }

  private withQuery(url: string, params: Record<string, string | number | boolean>): string {
    const qs = new URLSearchParams(
      Object.entries(params).reduce<Record<string, string>>((acc, [key, value]) => {
        acc[key] = String(value);
        return acc;
      }, {})
    ).toString();
    if (!qs) return url;
    return url.includes('?') ? `${url}&${qs}` : `${url}?${qs}`;
  }

  public async getMunicipioByCodPostal(cod_postal: string): Promise<Municipio> {
    const url = this.withQuery(this.apiUrl.getMunicipiosApi, { cod_postal });
    const datos = await lastValueFrom(this.http.get<Municipio>(url));
    return datos;
  }

  /** Genera el siguiente ID_FARMA dado un código de municipio */
  public async generarIdFarma(codMunicipio: string): Promise<GenerateIdResponse> {
    try {
      const body = { cod_municipio: codMunicipio };
      const result$ = this.http.post<GenerateIdResponse>(this.apiUrl.postGenerarIdApi, body);
      this.logger.debug('Llamando a', result$);
      return await lastValueFrom(result$);
    } catch (error) {
      this.logger.error('Error al generar ID_FARMA:', error);
      throw error;
    }
  }

  public async getPlaceId(id_google: string): Promise<PlaceIdResponse> {
    const url = this.withQuery(this.apiUrl.obtenerPlaceIdAPI, { id_google });
    const datos = await lastValueFrom(this.http.get<PlaceIdResponse>(url));
    return datos;
  }

  /** Lee las coordenadas de las farmacias registradas */
  public async getMapCoords(): Promise<MapCoords[]> {
    const url = `${this.apiUrl.mapCoordsAPI}`;
    const datos = await lastValueFrom(this.http.get<MapCoords[]>(url));
    return datos;
  }

  /** Lee las coordenadas de la competencia registradas */
  getMapCoordsCompetencia(coordenadas: string): Observable<MapCoordsCompetencia[]> {
    // normaliza: quita espacios
    const clean = coordenadas.replace(/\s+/g, '');
    const url = this.withQuery(this.apiUrl.mapCoordsCompetenciaAPI, { coordenadas: clean });
    const datos = this.http.get<MapCoordsCompetencia[]>(url);
    return datos;
  }

  public async updatePackage(data: PackageInfoStructure): Promise<void> {
    try {
      await lastValueFrom(this.http.put<PackageInfoStructure>(this.apiUrl.updatePackageApi, data));
      // Aquí podrías optar por emitir una alerta de éxito directamente:
      // this.showAlert({ type: 'success', summary: 'Éxito', detail: 'Farmacia agregada con éxito' });
    } catch (error) {
      this.logger.error('Error al agregar farmacia:', error);
      // También podrías emitir la alerta de error aquí:
      // this.showAlert({ type: 'error', summary: 'Error', detail: 'Error al agregar farmacia' });
      throw error;
    }
  }

  public async updatePharmacy(data: PharmaInfoCreate): Promise<PharmaInfoStructure> {
    try {
      const updated = await lastValueFrom(
        this.http.put<PharmaInfoStructure>(this.apiUrl.updatePharmacyApi, data)
      );
      return updated;
      // Aquí podrías optar por emitir una alerta de éxito directamente:
      // this.showAlert({ type: 'success', summary: 'Éxito', detail: 'Farmacia agregada con éxito' });
    } catch (error) {
      this.logger.error('Error al agregar farmacia:', error);
      // También podrías emitir la alerta de error aquí:
      // this.showAlert({ type: 'error', summary: 'Error', detail: 'Error al agregar farmacia' });
      throw error;
    }
  }

  public async deleteFarmacia(id_farma: string): Promise<void> {
    try {
      await lastValueFrom(this.http.delete<PharmaInfoStructure>(`${this.apiUrl.deletePharmacyApi}/${id_farma}`));
      // Aquí podrías optar por emitir una alerta de éxito directamente:
      // this.showAlert({ type: 'success', summary: 'Éxito', detail: 'Farmacia eliminada con éxito' });
    } catch (error) {
      this.logger.error('Error al eliminar farmacia:', error);
      // También podrías emitir la alerta de error aquí:
      // this.showAlert({ type: 'error', summary: 'Error', detail: 'Error al eliminar farmacia' });
      throw error;
    }
  }

  public async deletePaquete(id_farma: string): Promise<void> {
    try {
      await lastValueFrom(this.http.delete<void>(`${this.apiUrl.deletePackageApi}/${id_farma}`));
      // Aquí podrías optar por emitir una alerta de éxito directamente:
      // this.showAlert({ type: 'success', summary: 'Éxito', detail: 'Paquete eliminado con éxito' });
    } catch (error) {
      this.logger.error('Error al eliminar paquete:', error);
      // También podrías emitir la alerta de error aquí:
      // this.showAlert({ type: 'error', summary: 'Error', detail: 'Error al eliminar paquete' });
      throw error;
    }
  }

  public async getGroupCatalog(): Promise<PharmaGroupCatalogResponse> {
    return lastValueFrom(this.http.get<PharmaGroupCatalogResponse>(this.apiUrl.groupCatalogApi));
  }

  public async createGroupCatalog(data: PharmaGroupCatalogRequest): Promise<PharmaGroupCatalog> {
    return lastValueFrom(this.http.post<PharmaGroupCatalog>(this.apiUrl.groupCatalogApi, data));
  }

  public async updateGroupCatalog(
    idGrupo: number,
    data: PharmaGroupCatalogRequest
  ): Promise<PharmaGroupCatalog> {
    return lastValueFrom(
      this.http.put<PharmaGroupCatalog>(`${this.apiUrl.groupCatalogApi}/${idGrupo}`, data)
    );
  }

  public async deleteGroupCatalog(idGrupo: number): Promise<void> {
    await lastValueFrom(this.http.delete<void>(`${this.apiUrl.groupCatalogApi}/${idGrupo}`));
  }

  public async getGroupMemberships(): Promise<PharmaGroupMembershipResponse> {
    return lastValueFrom(
      this.http.get<PharmaGroupMembershipResponse>(this.apiUrl.groupMembershipsApi)
    );
  }

  public async createGroupMembership(
    data: PharmaGroupMembershipRequest
  ): Promise<PharmaGroupMembership> {
    return lastValueFrom(
      this.http.post<PharmaGroupMembership>(this.apiUrl.groupMembershipsApi, data)
    );
  }

  public async updateGroupMembership(
    original: Pick<PharmaGroupMembership, 'id_farma' | 'id_grupo'>,
    data: PharmaGroupMembershipRequest
  ): Promise<PharmaGroupMembership> {
    const idFarma = encodeURIComponent(original.id_farma);
    return lastValueFrom(
      this.http.put<PharmaGroupMembership>(
        `${this.apiUrl.groupMembershipsApi}/${idFarma}/${original.id_grupo}`,
        data
      )
    );
  }

  public async deleteGroupMembership(idFarma: string, idGrupo: number): Promise<void> {
    await lastValueFrom(
      this.http.delete<void>(
        `${this.apiUrl.groupMembershipsApi}/${encodeURIComponent(idFarma)}/${idGrupo}`
      )
    );
  }



  public async addOrden(data: RegisterOrder): Promise<void> {
    try {
      await lastValueFrom(this.http.post<RegisterOrder>(this.apiUrl.addExecutionRegApi, data));
      // Aquí podrías optar por emitir una alerta de éxito directamente:
      // this.showAlert({ type: 'success', summary: 'Éxito', detail: 'Orden agregada con éxito' });
    } catch (error) {
      this.logger.error('Error al agregar orden:', error);
      // También podrías emitir la alerta de error aquí:
      // this.showAlert({ type: 'error', summary: 'Error', detail: 'Error al agregar orden' });
      throw error;
    }
  }

  // ALERTAS
  alert$ = this.alertService.alert$;

  showAlert(message: AlertMessage, autoCloseMs?: number) {
    this.alertService.showAlert(message, autoCloseMs);
  }

  clearAlert() {
    this.alertService.clearAlert();
  }
  extractDate(fecha: string): string {
    return formatDateYmd(fecha);
  }

  extractTime(fecha: string): string {
    return formatTimeHms(fecha);
  }


  //WHATSAPP

  getConversationList(): Observable<ConversationList[]> {
    return this.whatsappService.getConversationList();
  }

  getWhatsappDetails(phone: string): Observable<Message[]> {
    return this.whatsappService.getWhatsappDetails(phone);
  }

  sendWhatsappMessage(phone: string, body: string, context_message_id?: string | null) {
    return this.whatsappService.sendWhatsappMessage(phone, body, context_message_id);
  }

  deleteWhatsappMessage(messageId: string) {
  return this.whatsappService.deleteWhatsappMessage(messageId);
}

  // DEBUG
  getPolling(): Observable<PollingResponse> {
    return this.debugService.getPolling();
  }

  public get partialUpload(): BehaviorSubject<PartialUploadResponse | undefined> {
    return this.uploadService.partialUpload;
  }

  public set partialUpload(value: PartialUploadResponse) {
    this.uploadService.partialUpload.next(value);
  }

  getPartialUpload(): Observable<PartialUploadResponse> {
    return this.uploadService.getPartialUpload();
  }

  public async getUploadStatusDatabricks(): Promise<UploadStatusRow[]> {
    return this.uploadService.getUploadStatusDatabricks();
  }

  public get logData(): BehaviorSubject<LogResponse | undefined> {
    return this.debugService.logData;
  }

  public set logData(value: LogResponse) {
    this.debugService.logData = value;
  }

  public async getLogData(
    farmacia_nom: string,
    tipo: 'instalador' | 'launcher' | 'acumulado' | 'motor' | 'asistente' | 'error' = 'motor'
  ) {
    return this.debugService.getLogData(farmacia_nom, tipo);
  }

  getFarmaIaMetadata(): Observable<FarmaIaMetadataEntry[]> {
    return this.farmaIaService.getFarmaIaMetadata().pipe(map((response) => response.data ?? []));
  }

  getFarmaIaTrainingData(): Observable<FarmaIaTrainingEntry[]> {
    return this.farmaIaService.getFarmaIaTrainingData();
  }

  public async updateSQLTraining(data: TrainingUpdateRequest): Promise<void> {
      await this.farmaIaService.updateSQLTraining(data);
  }

  embedFarmaIATraining() {
    return this.farmaIaService.embedFarmaIATraining();
  }


  getUsageData(): Observable<UsageDataEntry[]> {
    return this.farmaIaService.getUsageData();
  }

  public obtenerListadoNombres(): Observable<ListadoNombres> {
    const url = `${this.apiUrl.obtenerNombresIds}`;
    return this.http.get<ListadoNombres>(url);
  }

  getVacationPeople(includeInactive = false): Observable<VacationPerson[]> {
    const url = this.withQuery(this.apiUrl.vacationsPeopleApi, {
      include_inactive: includeInactive,
    });
    return this.http.get<VacationPerson[]>(url);
  }

  createVacationPerson(data: VacationPersonCreate): Observable<VacationPerson> {
    return this.http.post<VacationPerson>(this.apiUrl.vacationsPeopleApi, data);
  }

  updateVacationPerson(personId: number, data: VacationPersonUpdate): Observable<VacationPerson> {
    return this.http.put<VacationPerson>(`${this.apiUrl.vacationsPeopleApi}/${personId}`, data);
  }

  deactivateVacationPerson(personId: number): Observable<VacationPerson> {
    return this.http.delete<VacationPerson>(`${this.apiUrl.vacationsPeopleApi}/${personId}`);
  }

  getVacationCalendar(year: number, month: number): Observable<VacationCalendarResponse> {
    const url = this.withQuery(this.apiUrl.vacationsCalendarApi, { year, month });
    return this.http.get<VacationCalendarResponse>(url);
  }

  getVacationSummary(year: number): Observable<VacationSummary[]> {
    const url = this.withQuery(this.apiUrl.vacationsSummaryApi, { year });
    return this.http.get<VacationSummary[]>(url);
  }

  createVacationDay(data: VacationDayCreate): Observable<VacationDay> {
    return this.http.post<VacationDay>(this.apiUrl.vacationsDaysApi, data);
  }

  createVacationRange(data: VacationDayRangeCreate): Observable<VacationDay[]> {
    return this.http.post<VacationDay[]>(this.apiUrl.vacationsDaysRangeApi, data);
  }

  deleteVacationDay(dayId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl.vacationsDaysApi}/${dayId}`);
  }
}
